/**
 * Email Accounts List
 * Displays connected email accounts with actions
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EmailAccountCard } from "./email-account-card";
import { EmailAccountConfigureModal } from "./email-account-configure-modal";
import { EmailAccountRemoveModal } from "./email-account-remove-modal";
import { EmailAccountStopSyncModal } from "./email-account-stop-sync-modal";
import type { EmailAccountResponse } from "@/types/api/email";
import {
    useDeleteEmailAccount,
    useUpdateEmailAccount,
    useProcessAccountEmails,
    useGetGmailAuthUrl,
    useGetOutlookAuthUrl,
    useToggleEmailAccountSync,
} from "@/hooks/api/use-email-accounts";
import { getEmailAccountFolders } from "@/data/api/email";
import { toast } from "@/lib/toast-service";

interface EmailAccountsListProps {
    accounts: EmailAccountResponse[];
    isLoading: boolean;
    onRefresh: () => void;
    accountToConfigure?: string | null;
    onConfigureComplete?: () => void;
}

export const EmailAccountsList = ({
    accounts,
    isLoading,
    onRefresh,
    accountToConfigure,
    onConfigureComplete,
}: EmailAccountsListProps) => {
    const { t } = useTranslation(["pages:email-settings"]);
    const [configureAccount, setConfigureAccount] = useState<EmailAccountResponse | null>(null);
    const [removeAccount, setRemoveAccount] = useState<EmailAccountResponse | null>(null);
    const [stopSyncAccount, setStopSyncAccount] = useState<EmailAccountResponse | null>(null);
    const [syncingAccountId, setSyncingAccountId] = useState<string | null>(null);
    const [togglingAccountId, setTogglingAccountId] = useState<string | null>(null);

    // Handle external account configuration request
    // Pre-load folders before opening the modal
    useEffect(() => {
        if (accountToConfigure && accounts && accounts.length > 0) {
            const account = accounts.find(acc => acc.account_id === accountToConfigure);
            if (account) {
                // Pre-load folders before opening the modal
                getEmailAccountFolders(account.account_id)
                    .then(() => {
                        // Folders loaded successfully, now open the modal
                        setConfigureAccount(account);
                    })
                    .catch((error) => {
                        // Even if folders fail to load, open the modal
                        // The modal will handle the error state
                        console.warn("Failed to pre-load folders:", error);
                        setConfigureAccount(account);
                    });
            }
        }
    }, [accountToConfigure, accounts]);

    const deleteAccountMutation = useDeleteEmailAccount();
    const updateAccountMutation = useUpdateEmailAccount();
    const processEmailsMutation = useProcessAccountEmails();
    const getGmailAuthUrlMutation = useGetGmailAuthUrl();
    const getOutlookAuthUrlMutation = useGetOutlookAuthUrl();
    const toggleSyncMutation = useToggleEmailAccountSync();

    const handleConfigure = (account: EmailAccountResponse) => {
        setConfigureAccount(account);
    };

    const handleRemove = (account: EmailAccountResponse) => {
        setRemoveAccount(account);
    };

    const handleReconnect = async (account: EmailAccountResponse) => {
        try {
            if (account.provider === "gmail") {
                const authResponse = await getGmailAuthUrlMutation.mutateAsync(account.account_id);
                window.location.href = authResponse.authorization_url;
            } else if (account.provider === "outlook") {
                const authResponse = await getOutlookAuthUrlMutation.mutateAsync(account.account_id);
                window.location.href = authResponse.authorization_url;
            } else {
                toast.error(t("errors.oauthFailed"));
            }
        } catch (error) {
            toast.error(t("errors.oauthFailed"));
        }
    };

    const handleSync = async (account: EmailAccountResponse) => {
        setSyncingAccountId(account.account_id);
        toast.success(t("accountsList.syncStarted"));
        try {
            await processEmailsMutation.mutateAsync(account.account_id);
            onRefresh();
        } catch (error) {
            toast.error(t("errors.syncFailed"));
        } finally {
            setSyncingAccountId(null);
        }
    };

    const handleToggleSync = async (account: EmailAccountResponse, enabled: boolean) => {
        setTogglingAccountId(account.account_id);
        try {
            await toggleSyncMutation.mutateAsync({
                accountId: account.account_id,
                enabled,
            });
            toast.success(enabled ? t("accountsList.syncEnabled") : t("accountsList.syncDisabled"));
            onRefresh();
        } catch (error) {
            toast.error(t("errors.toggleSync"));
        } finally {
            setTogglingAccountId(null);
        }
    };

    const handleConfirmRemove = async () => {
        if (!removeAccount) return;
        try {
            await deleteAccountMutation.mutateAsync(removeAccount.account_id);
            toast.success("Account rimosso con successo");
            setRemoveAccount(null);
            onRefresh();
        } catch (error) {
            toast.error(t("errors.deleteAccount"));
        }
    };

    const handleStopSync = (account: EmailAccountResponse) => {
        setStopSyncAccount(account);
    };

    const handleConfirmStopSync = async () => {
        if (!stopSyncAccount) return;
        try {
            await toggleSyncMutation.mutateAsync({
                accountId: stopSyncAccount.account_id,
                enabled: false,
            });
            toast.success(t("accountsList.syncDisabled"));
            setStopSyncAccount(null);
            onRefresh();
        } catch (error) {
            toast.error(t("errors.toggleSync"));
            setStopSyncAccount(null);
        }
    };

    const handleSaveConfigure = async (data: {
        display_name?: string;
        auto_process_attachments?: boolean;
        allowed_sender_domains?: string[];
        folders_to_watch?: string[];
        sync_from_date?: string | null;
    }) => {
        if (!configureAccount) return;
        try {
            await updateAccountMutation.mutateAsync({
                accountId: configureAccount.account_id,
                data,
            });
            toast.success("Configurazione salvata");
            // Don't close modal here - it will be closed after sync confirmation
            // The modal will handle closing after sync confirmation
        } catch (error) {
            toast.error(t("errors.updateAccount"));
        }
    };

    const handleConfigureClose = () => {
        setConfigureAccount(null);
        if (onConfigureComplete) {
            onConfigureComplete();
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[1, 2].map((i) => (
                    <div
                        key={i}
                        className="animate-pulse rounded-xl bg-secondary p-4 h-24 ring-1 ring-tertiary"
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {accounts.map((account) => (
                    <EmailAccountCard
                        key={account.account_id}
                        account={account}
                        onConfigure={handleConfigure}
                        onRemove={handleRemove}
                        onReconnect={handleReconnect}
                        onSync={handleSync}
                        onToggleSync={handleToggleSync}
                        onStopSync={handleStopSync}
                        isSyncing={syncingAccountId === account.account_id}
                        isToggling={togglingAccountId === account.account_id}
                    />
                ))}
            </div>

            {/* Configure Modal */}
            {configureAccount && (
                <EmailAccountConfigureModal
                    account={configureAccount}
                    isOpen={!!configureAccount}
                    onClose={handleConfigureClose}
                    onSave={handleSaveConfigure}
                    onSync={handleSync}
                    isLoading={updateAccountMutation.isPending}
                />
            )}

            {/* Remove Confirmation Modal */}
            {removeAccount && (
                <EmailAccountRemoveModal
                    account={removeAccount}
                    isOpen={!!removeAccount}
                    onClose={() => setRemoveAccount(null)}
                    onConfirm={handleConfirmRemove}
                    isLoading={deleteAccountMutation.isPending}
                />
            )}

            {/* Stop Sync Confirmation Modal */}
            {stopSyncAccount && (
                <EmailAccountStopSyncModal
                    account={stopSyncAccount}
                    isOpen={!!stopSyncAccount}
                    onClose={() => setStopSyncAccount(null)}
                    onConfirm={handleConfirmStopSync}
                    isLoading={toggleSyncMutation.isPending}
                />
            )}
        </>
    );
};
